<template>
  <div class="pollstar-container">
    <div class="pollstar-container2">
    <!-- <div class="background-gradient"></div> -->
    <br />
    <br />
    <div class="pollstar-card">
      <v-app-bar dark flat dense class="header-bar">
        <v-toolbar-title class="title-glow">🤖 Pollstar Artist AI Search</v-toolbar-title>
        <v-spacer />
        <v-switch
          v-model="darkTheme"
          inset
          hide-details
          aria-label="Toggle dark/light theme"
          @change="toggleTheme"
        >
          <template #label>
            <v-icon>{{ darkTheme ? 'mdi-weather-night' : 'mdi-white-balance-sunny' }}</v-icon>
          </template>
        </v-switch>
        <v-btn icon @click="copyShareLink" aria-label="Share artist link">
          <v-icon>mdi-share-variant</v-icon>
        </v-btn>
        <v-btn icon @click="exportPDF" :disabled="!artistDetails" aria-label="Export PDF report">
          <v-icon>mdi-file-pdf</v-icon>
        </v-btn>
        <v-btn icon @click="toggleAssistant" aria-label="Toggle voice assistant">
          <v-icon class="assistant-icon" :class="{ listening: assistantActive }">mdi-robot-voice</v-icon>
        </v-btn>
      </v-app-bar>

      <v-card-text>
        <v-stepper v-model="currentStep" class="stepper-glow">
          <v-stepper-header>
            <v-stepper-step :complete="currentStep>1" step="1">Search</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="currentStep>2" step="2">Results</v-stepper-step>
            <v-divider />
            <v-stepper-step step="3">Details</v-stepper-step>
          </v-stepper-header>
        </v-stepper>
        <v-divider class="mb-4" />
        <v-row class="search-row" align="center">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="artistName"
              label="Enter artist name"
              dense
              outlined
              dark
              :loading="loading"
              @input="onArtistNameInput"
              @keyup.enter="searchArtists"
              clearable
              aria-label="Artist name input"
            >
              <template #append>
                <v-icon class="search-icon">mdi-magnify</v-icon>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn large block class="btn-glow" :loading="loading" @click="searchArtists" aria-label="Search artists">
              Search
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="artists.length && currentStep===2" class="filter-row">
          <v-col cols="12" md="6">
            <v-checkbox-group v-model="selectedGenres" column aria-label="Filter by genre">
              <v-checkbox v-for="genre in availableGenres" :key="genre" :value="genre" :label="genre" />
            </v-checkbox-group>
          </v-col>
          <v-col cols="12" md="6">
            <v-range-slider
              v-model="selectedYearRange"
              :min="minYear"
              :max="maxYear"
              dense
              hide-details
              aria-label="Filter events by year range"
              thumb-label
            />
          </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate height="4" class="mb-4" />
      </v-card-text>

      <transition name="fade">
        <v-alert v-if="error" type="error" dense class="mx-4 neon-alert" role="alert">
          {{ error }}
        </v-alert>
      </transition>
      <transition name="fade">
        <v-alert v-if="notification" type="success" dense class="mx-4 neon-alert" role="status">
          {{ notification }}
        </v-alert>
      </transition>

      <v-card-text v-if="filteredArtists.length && currentStep===2" class="artist-list-grid">
        <h3 class="grid-title">Search Results</h3>
        <v-row dense>
          <v-col v-for="artist in filteredArtists" :key="artist.artistId" cols="12" sm="6" md="4">
            <v-card
              class="ai-artist-card"
              tabindex="0"
              @click="handleArtistClick(artist.artistId)"
              @keydown.enter="handleArtistClick(artist.artistId)"
              :aria-label="`View details for ${artist.artistName}`"
            >
              <v-avatar size="48" class="mr-3">
                <img :src="artist.pictureUrl || fallbackImage" alt="Artist" />
              </v-avatar>
              <div>
                <div class="artist-name">{{ artist.artistName }}</div>
                <div class="artist-genre">{{ artist.genre || 'Unknown' }}</div>
              </div>
              <v-icon
                class="favorite-icon"
                :class="{ saved: favorites.some(a=>a.artistId===artist.artistId) }"
                @click.stop="toggleFavorite(artist)"
                aria-label="Toggle favorite"
              >mdi-heart</v-icon>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text v-if="artistDetails && currentStep===3" class="artist-details-section">
        <h3 class="grid-title">Artist Details</h3>
        <v-row>
          <v-col cols="12" sm="6" md="4" v-for="(val,label) in detailFields" :key="label">
            <div class="detail-label">{{ label }}:</div>
            <div class="detail-value" v-html="val"></div>
          </v-col>
        </v-row>

        <div class="artist-contacts" v-if="artistContacts.length">
    <h3 class="grid-title">🧠 Artist Contacts</h3>
    <v-row dense>
      <v-col
        v-for="contact in artistContacts"
        :key="contact.id"
        cols="12"
        sm="6"
        md="6"
        class="contact-card"
      >
        <v-card dark class="contact-panel" elevation="10">
          <v-card-title class="contact-name">{{ contact.name }}</v-card-title>
          <v-card-subtitle class="contact-territory">🌍 {{ contact.territory }}</v-card-subtitle>

          <v-card-text>
            <p><strong>📇 Company Types:</strong> {{ contact.companyTypes.map(ct => ct.type).join(', ') }}</p>
            <p><strong>📞 Phone(s):</strong> {{ contact.phones.map(p => p.number).join(', ') }}</p>
            <p><strong>📍 Address:</strong>
              <br />
              <span v-for="a in contact.addresses" :key="a.formattedAddress">{{ a.formattedAddress }}</span>
            </p>
            <p><strong>🔗 Links:</strong>
              <br />
              <span
                v-for="link in contact.links"
                :key="link.value"
                class="contact-link"
              >
                <a
                  :href="link.value.startsWith('http') ? link.value : 'https://' + link.value"
                  target="_blank"
                >
                  {{ link.value }}
                </a>
              </span>
            </p>
             <!-- Websites -->
  <p v-if="contact.websites.length">
    <strong>🌐 Website(s):</strong>
    <span v-for="w in contact.websites" :key="w" class="contact-link">
      <a :href="w.startsWith('http') ? w : 'https://' + w" target="_blank">
        {{ w }}
      </a>
    </span>
  </p>

  <!-- Social Media -->
  <p v-if="contact.socialMedia.length">
    <strong>🤳 Social:</strong>
    <span
      v-for="link in contact.socialMedia"
      :key="link.url"
      class="contact-link"
    >
      <a
        :href="link.url.startsWith('http') ? link.url : 'https://' + link.url"
        target="_blank"
      >
        {{ link.type }}
      </a>
    </span>
  </p>
          </v-card-text>

          <v-divider class="my-2" color="#31f0ff" />

          <v-card-text v-if="contact.contacts && contact.contacts.length">
  <h4 class="staff-header">👥 Staff Contacts:</h4>
  <v-list dense class="staff-list">
    <v-list-item
      v-for="staff in contact.contacts"
      :key="staff.id"
      class="staff-item"
    >
      <v-list-item-content>

        <!-- Name & Title -->
        <v-list-item-title class="staff-name">
          {{ staff.name }} – {{ staff.title }}
        </v-list-item-title>

        <!-- Emails -->
        <v-list-item-subtitle
          v-if="staff.emails.length"
          class="staff-emails"
        >
          <strong>✉️ Email:</strong> {{ staff.emails.join(', ') }}
        </v-list-item-subtitle>

        <!-- Phones -->
        <v-list-item-subtitle
          v-if="staff.phones.length"
          class="staff-phones"
        >
          <strong>📞 Phone:</strong> {{ staff.phones.join(', ') }}
        </v-list-item-subtitle>

        <!-- Addresses -->
        <v-list-item-subtitle
          v-if="staff.addresses.length"
          class="staff-addresses"
        >
          <strong>📍 Address:</strong>
          <div
            v-for="addr in staff.addresses"
            :key="addr"
            class="staff-address"
          >
            {{ addr }}
          </div>
        </v-list-item-subtitle>

        <!-- Websites -->
        <v-list-item-subtitle
          v-if="staff.websites.length"
          class="staff-websites"
        >
          <strong>🌐 Website:</strong>
          <span v-for="w in staff.websites" :key="w">
            <a
              :href="w.startsWith('http') ? w : 'https://' + w"
              target="_blank"
            >
              {{ w }}
            </a>
          </span>
        </v-list-item-subtitle>

        <!-- Social Media -->
        <v-list-item-subtitle
          v-if="staff.socialMedia.length"
          class="staff-social"
        >
          <strong>🤳 Social:</strong>
          <span
            v-for="link in staff.socialMedia"
            :key="link.url"
            class="contact-link"
          >
            <a
              :href="link.url.startsWith('http') ? link.url : 'https://' + link.url"
              target="_blank"
            >
              {{ link.type }}
            </a>
          </span>
        </v-list-item-subtitle>

      </v-list-item-content>
    </v-list-item>
  </v-list>
</v-card-text>

        </v-card>
      </v-col>
    </v-row>
  </div>

          <!-- toggle future/past -->
    <v-row align="center" class="mb-4">
      <v-col cols="12" md="4">
        <v-switch v-model="showFuture" label="Show Future Only" inset />
      </v-col>
      <v-col cols="12" md="8" class="neon-alert">
        <v-progress-linear
          :value="100 * futureEvents.length / allEvents.length"
          height="8"
          color="success"
          background-color="grey darken-3"
        >
          <strong class="white--text">
            {{ futureEvents.length }} Upcoming / {{ allEvents.length }} Total
          </strong>
        </v-progress-linear>
      </v-col>
    </v-row>

    <!-- frequency chart -->
    <!-- <v-card flat class="mb-6">
      <v-card-title>📈 Event Frequency</v-card-title>
      <v-card-text style="height:250px">
        <tour-linechart :chartData="frequencyData" />
      </v-card-text>
    </v-card> -->
    <tour-linechart :chartData="frequencyData" class="mb-6" />
    <!-- events by year -->
    <h3 class="grid-title">Events by Year</h3>
    <v-expansion-panels multiple>
      <v-expansion-panel v-for="(evs,yr) in eventsByYear" :key="yr">
        <v-expansion-panel-header>
          {{ yr }} ({{ evs.length }})
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <ul class="events-list">
            <li
              v-for="e in evs.filter(e => !showFuture || new Date(e.eventDate)>new Date())"
              :key="e.eventId"
            >
              {{ e.eventDate }} – <strong>{{ e.name }}</strong>
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

        <h3 class="grid-title">Tour Map</h3>
        <tour-worldmap
          :locations="filteredMarkers"
          :showFuture="showFuture"
          :darkTheme="darkTheme"
          class="mb-6"
        ></tour-worldmap>

      </v-card-text>
    </div>
  </div>
  </div>
  
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
// Import Sentry from the npm package
// import * as Sentry from '@sentry/browser';
import TourLinechart from './TourLineChartNew.vue' 
import TourWorldmap from './TourWorldMapPollstar.vue';
import debounce from 'lodash.debounce';

export default {
  name: 'PollstarArtistSearch',
  components: { TourWorldmap, TourLinechart },
  data() {
    return {
      pastEvents: [],
      futureEvents: [],
      allEvents: [],
      frequencyData: { labels: [], datasets: [] },
      showFuture: false,
      artistName: '', artists: [], favorites: JSON.parse(localStorage.getItem('favorites')||'[]'),
      artistDetails: null, artistContacts: [], artistEvents: [], fullMarkers: [],
      loading: false, error: null, notification: null, currentStep: 1,
      fallbackImage: 'https://gallery.yopriceville.com/var/resizes/Free-Clipart-Pictures/Music-PNG/Gramophone_Vinyl_LP_Record_PNG_Transparent_Clip_Art_Image.png',
      assistantActive: false, recognition: null,
      selectedGenres: [], selectedYearRange: [0,0], darkTheme: JSON.parse(localStorage.getItem('darkTheme')||'false'),
    };
  },
  created() {
    // Sentry.init({ dsn: 'YOUR_SENTRY_DSN' });
    this.$vuetify.theme.dark = this.darkTheme;
    this.debouncedSearch = debounce(this.searchArtists, 500);
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown);
  },
  computed: {
    availableGenres() {
      return [...new Set(this.artists.map(a=>a.genre).filter(g=>g))];
    },
    minYear() {
    const ys = this.allEvents.map(e=>parseInt(e.eventDate.slice(0,4))).filter(y=>y);
    return ys.length ? Math.min(...ys) : new Date().getFullYear();
  },
  maxYear() {
    const ys = this.allEvents.map(e=>parseInt(e.eventDate.slice(0,4))).filter(y=>y);
    return ys.length ? Math.max(...ys) : new Date().getFullYear();
  },
    filteredArtists() {
      return this.artists.filter(a=>{
        return (!this.selectedGenres.length||this.selectedGenres.includes(a.genre));
      });
    },
    filteredMarkers() {
    const [low,high] = this.selectedYearRange;
    return this.fullMarkers.filter(m => m.year >= low && m.year <= high);
  },
    eventsByYear() {
      return this.allEvents.reduce((acc, e) => {
        const y = e.eventDate.slice(0,4) || 'Unknown';
        (acc[y] = acc[y] || []).push(e);
        return acc;
      }, {});
    },
    detailFields() {
      const d=this.artistDetails;
      return {
        'Name':d.name,'Type':d.artistType,'Genres':d.genre,
        'Events':this.artistEvents.length,'Locations':this.fullMarkers.length,
        'Website':d.website!=='N/A'?`<a href="${d.website}" target="_blank" class="link-glow">${d.website}</a>`:'N/A',
        'Pollstar URL':`<a href="${d.url}" target="_blank" class="link-glow">${d.url}</a>`
      };
    }
  },
  methods: {
    onArtistNameInput() { this.error=this.notification=null; if(this.artistName.trim()) this.debouncedSearch(); },
    async searchArtists() {
      if(!this.artistName.trim()){ this.error='Enter artist name.'; return; }
      this.currentStep=2; this.loading=true; this.artists=[]; this.artistDetails=this.artistContacts=this.artistEvents=this.fullMarkers=[];
      try{
        const {data} = await axios.get(`${process.env.VUE_APP_NODE_SERVER}pollstar/artist/search`,{params:{artistName:this.artistName}});
        this.artists=data.artists||[];
        this.notification=this.artists.length?'Artists loaded!':'No artists found.';
      }catch(e){ this.Sentry.captureException(e); this.error='Search failed.';}finally{ this.loading=false; setTimeout(()=>{this.error=this.notification=null},3000); }
    },
    async handleArtistClick(id) {
  this.currentStep   = 3;
  this.loading       = true;

  // 0) Clear out any previous data
  this.artistDetails   = null;
  this.artistContacts  = [];
  this.pastEvents      = [];
  this.futureEvents    = [];
  this.allEvents       = [];
  this.fullMarkers     = [];
  this.frequencyData   = { labels: [], datasets: [] };
  this.routeBook       = [];

  try {
    // 1) Fetch artist details
    const { data: details } = await axios.get(
      `${process.env.VUE_APP_NODE_SERVER}pollstar/artist/details/${id}`
    );
    this.artistDetails = {
      name:       details.name || 'N/A',
      artistType: details.artistType || 'N/A',
      genre:      Array.isArray(details.genres)
                   ? details.genres.join(', ')
                   : details.genres || 'N/A',
      website:    (details.links || [])
                   .find(l => l.type === 'Website')
                   ?.value || 'N/A',
      url:        details.url || 'N/A'
    };

    // 2) Fetch & map contacts
    const { data: contactsData } = await axios.get(
      `${process.env.VUE_APP_NODE_SERVER}pollstar/artist/contacts/${id}`
    );
    this.artistContacts = (contactsData.contacts || []).map(c => {
      const links = c.links || [];
      return {
        id:           c.id || c.contactId || c.name,
        name:         c.name || 'N/A',
        territory:    c.territory || 'N/A',
        companyTypes: c.companyTypes || [],
        phones:       (c.phones || []).map(p => p.number),
        emails:       Array.isArray(c.email) ? c.email : c.email ? [c.email] : [],
        addresses:    (c.addresses || []).map(a => a.formattedAddress),
        websites:     links.filter(l => /website/i.test(l.type)).map(l => l.value),
        socialMedia:  links
                       .filter(l => !/website/i.test(l.type))
                       .map(l => ({ type: l.type, url: l.value })),

        // nested staff roster
        contacts: (c.contacts || []).map(s => {
          const staffLinks = s.links || [];
          return {
            id:          s.id || s.staffId || s.name,
            name:        s.name || 'N/A',
            title:       s.title || 'N/A',
            phones:      Array.isArray(s.phone) ? s.phone : s.phone ? [s.phone] : [],
            emails:      Array.isArray(s.email) ? s.email : s.email ? [s.email] : [],
            addresses:   (s.addresses || []).map(a => a.formattedAddress),
            websites:    staffLinks
                           .filter(l => /website/i.test(l.type))
                           .map(l => l.value),
            socialMedia: staffLinks
                           .filter(l => !/website/i.test(l.type))
                           .map(l => ({ type: l.type, url: l.value }))
          };
        })
      };
    });

    // 3) Fetch past & future events in parallel (using Pollstar API modes)
    const [pastRes, futureRes] = await Promise.all([
      axios.get(
        `${process.env.VUE_APP_NODE_SERVER}pollstar/artist/events/${id}`, {
          params: { mode: 'past',   page: 0, pageSize: 500 }
        }
      ),
      axios.get(
        `${process.env.VUE_APP_NODE_SERVER}pollstar/artist/events/${id}`, {
          params: { mode: 'future', page: 0, pageSize: 500 }
        }
      )
    ]);
    this.pastEvents   = pastRes.data.events   || [];
    this.futureEvents = futureRes.data.events || [];

    // 4) Merge & sort allEvents chronologically
    this.allEvents = [...this.pastEvents, ...this.futureEvents]
      .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));
      this.selectedYearRange = [ this.minYear, this.maxYear ];
    // 5) Build the frequencyData for the line chart
    const countsByYear = this.allEvents.reduce((acc, ev) => {
      const yr = ev.eventDate.slice(0, 4) || 'Unknown';
      acc[yr] = (acc[yr] || 0) + 1;
      return acc;
    }, {});
    const years = Object.keys(countsByYear).sort();
    this.frequencyData = {
      labels: years,
      datasets: [{
        label: 'Events per Year',
        data: years.map(y => countsByYear[y]),
        borderWidth: 2
      }]
    };

    // 6) Build fullMarkers (with isFuture flag) for the map
    this.fullMarkers = this.allEvents
      .filter(ev => ev.latitude && ev.longitude)
      .map(ev => ({
        year:     +ev.eventDate.slice(0, 4),
        latlng:   [parseFloat(ev.latitude), parseFloat(ev.longitude)],
        popup:    `${ev.eventDate}${ev.name}`,
        isFuture: new Date(ev.eventDate) > new Date()
      }));

    // 7) Initialize the year‐slider range based on allEvents
    this.selectedYearRange = [ this.minYear, this.maxYear ];

    // 8) Optional: fetch route‑book (if available on your backend)
    try {
      const { data: rb } = await axios.get(
        `${process.env.VUE_APP_NODE_SERVER}pollstar/artist/routebook/${id}`
      );
      this.routeBook = rb.legs || [];
    } catch {
      // silently ignore if not available
    }

    this.notification = 'Details & events loaded!';
  } catch (err) {
    console.error(err);
    this.Sentry?.captureException(err);
    this.error = 'Load failed.';
  } finally {
    this.loading = false;
    // clear messages after a short delay
    setTimeout(() => {
      this.error = this.notification = null;
    }, 3000);
  }
},

  //   async handleArtistClick(id) {
  //   this.currentStep = 3;
  //   this.loading     = true;

  //   this.artistDetails  = null;
  //   this.artistContacts = [];
  //   this.pastEvents     = [];
  //   this.futureEvents   = [];
  //   this.allEvents      = [];
  //   this.fullMarkers    = [];
  //   this.frequencyData  = { labels: [], datasets: [] };
  //   this.routeBook      = [];

  //   try {

  //     const { data: details } = await axios.get(
  //       `${process.env.VUE_APP_NODE_SERVER}pollstar/artist/details/${id}`
  //     );
  //     this.artistDetails = {
  //       name:       details.name || 'N/A',
  //       artistType: details.artistType || 'N/A',
  //       genre:      Array.isArray(details.genres)
  //                    ? details.genres.join(', ')
  //                    : details.genres || 'N/A',
  //       website:    (details.links || [])
  //                    .find(l => l.type === 'Website')
  //                    ?.value || 'N/A',
  //       url:        details.url || 'N/A'
  //     };

  //     const { data: contactsData } = await axios.get(
  //       `${process.env.VUE_APP_NODE_SERVER}pollstar/artist/contacts/${id}`
  //     );
  //     this.artistContacts = (contactsData.contacts || []).map(c => {
  //       const links = c.links || [];
  //       return {
  //         id:         c.id || c.contactId || c.name,
  //         name:       c.name || 'N/A',
  //         territory:  c.territory || 'N/A',
  //         companyTypes: c.companyTypes || [],
  //         phones:     (c.phones || []).map(p => p.number),
  //         emails:     Array.isArray(c.email) ? c.email : c.email ? [c.email] : [],
  //         addresses:  (c.addresses || []).map(a => a.formattedAddress),
  //         websites:   links.filter(l => /website/i.test(l.type)).map(l => l.value),
  //         socialMedia:links
  //                      .filter(l => !/website/i.test(l.type))
  //                      .map(l => ({ type: l.type, url: l.value })),

     
  //         contacts: (c.contacts || []).map(s => {
  //           const staffLinks = s.links || [];
  //           return {
  //             id:       s.id || s.staffId || s.name,
  //             name:     s.name || 'N/A',
  //             title:    s.title || 'N/A',
  //             phones:   Array.isArray(s.phone) ? s.phone : s.phone ? [s.phone] : [],
  //             emails:   Array.isArray(s.email) ? s.email : s.email ? [s.email] : [],
  //             addresses:(s.addresses || []).map(a => a.formattedAddress),
  //             websites: staffLinks
  //                        .filter(l => /website/i.test(l.type))
  //                        .map(l => l.value),
  //             socialMedia: staffLinks
  //                          .filter(l => !/website/i.test(l.type))
  //                          .map(l => ({ type: l.type, url: l.value }))
  //           };
  //         })
  //       };
  //     });

  //     const [pastRes, futureRes] = await Promise.all([
  //       axios.get(`${process.env.VUE_APP_NODE_SERVER}pollstar/artist/events/${id}`, {
  //         params: { mode:'past',   page:0, pageSize:500 }
  //       }),
  //       axios.get(`${process.env.VUE_APP_NODE_SERVER}pollstar/artist/events/${id}`, {
  //         params: { mode:'future', page:0, pageSize:500 }
  //       })
  //     ]);

  //     this.pastEvents   = pastRes.data.events   || [];
  //     this.futureEvents = futureRes.data.events || [];

  //     this.allEvents = [...this.pastEvents, ...this.futureEvents]
  //       .sort((a,b) =>
  //         new Date(a.eventDate) - new Date(b.eventDate)
  //       );

  //     const counts = this.allEvents.reduce((acc, ev) => {
  //       const y = ev.eventDate.slice(0,4);
  //       acc[y] = (acc[y]||0) + 1;
  //       return acc;
  //     }, {});
  //     const years = Object.keys(counts).sort();
  //     this.frequencyData = {
  //       labels: years,
  //       datasets: [{
  //         label: 'Events per Year',
  //         data: years.map(y => counts[y])
  //       }]
  //     };

  //     this.fullMarkers = this.allEvents
  //       .filter(ev => ev.latitude && ev.longitude)
  //       .map(ev => ({
  //         year:     +ev.eventDate.slice(0,4),
  //         latlng:   [parseFloat(ev.latitude), parseFloat(ev.longitude)],
  //         popup:    `${ev.eventDate} – ${ev.name}`,
  //         isFuture: new Date(ev.eventDate) > new Date()
  //       }));

  //     this.selectedYearRange = [ this.minYear, this.maxYear ];

  //     try {
  //       const { data: rb } = await axios.get(
  //         `${process.env.VUE_APP_NODE_SERVER}pollstar/artist/routebook/${id}`
  //       );
  //       this.routeBook = rb.legs || [];
  //     } catch {
    
  //     }

  //     this.notification = 'Details & events loaded!';
  //   } catch (e) {
  //     this.Sentry?.captureException(e);
  //     this.error = 'Load failed.';
  //   } finally {
  //     this.loading = false;
  //     setTimeout(() => {
  //       this.error = this.notification = null;
  //     }, 3000);
  //   }
  // },
    toggleTheme(){ this.$vuetify.theme.dark=this.darkTheme; localStorage.setItem('darkTheme',JSON.stringify(this.darkTheme)); },
    copyShareLink(){ const link=`${window.location.origin}${window.location.pathname}?artist=${encodeURIComponent(this.artistName)}`; navigator.clipboard.writeText(link); this.notification='Link copied!'; setTimeout(()=>this.notification=null,2000); },
    exportPDF(){ if(!this.artistDetails)return; const doc=new jsPDF(); doc.setFontSize(18); doc.text(`${this.artistDetails.name} Report`,14,22); doc.setFontSize(12);
      let y=32; for(const [label,val] of Object.entries(this.detailFields)){ doc.text(`${label}:`,14,y); doc.text(val.replace(/<[^>]+>/g,''),50,y); y+=6; }
      y+=4; doc.text('Events:',14,y);
      this.artistEvents.forEach(ev=>{ y+=6; if(y>280){ doc.addPage(); y=20; } doc.text(`${ev.eventDate} – ${ev.name}`,20,y); }); doc.save(`${this.artistDetails.name}.pdf`);
    },
    toggleAssistant(){ this.assistantActive=!this.assistantActive; if(this.assistantActive) this.startSpeechRecognition(); else this.recognition?.stop(); },
    startSpeechRecognition(){ const SpeechRec=window.SpeechRecognition||window.webkitSpeechRecognition; if(!SpeechRec)return; this.recognition=new SpeechRec(); this.recognition.lang='en-US'; this.recognition.interimResults=false; this.recognition.onresult=e=>{ const cmd=e.results[0][0].transcript.toLowerCase(); if(cmd.startsWith('search ')){ this.artistName=cmd.replace('search ',''); this.searchArtists(); } this.recognition.stop(); this.assistantActive=false; };
      this.recognition.onerror=()=>{ this.assistantActive=false; };
      this.recognition.start();
    },
    handleKeydown(e){ if(e.key==='Escape' && this.currentStep===3){ this.artistDetails=null; this.currentStep=2; } },
    toggleFavorite(artist){ const idx=this.favorites.findIndex(a=>a.artistId===artist.artistId); if(idx>-1) this.favorites.splice(idx,1); else this.favorites.push(artist); localStorage.setItem('favorites',JSON.stringify(this.favorites)); },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700&family=Share+Tech+Mono&display=swap');
.pollstar-container {
  font-family: Arial, sans-serif;
  padding: 80px;
  max-width: 100%;
  margin: 0 auto;
  background: rgba(5, 3, 12, 0.5);
  border-radius: 18px;
  text-align: center;
}
.pollstar-container2 {
  font-family: Arial, sans-serif;
  padding: 0px;
  max-width: 90%;
  margin: -40px auto;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  color: #fff;
  margin-top: 0px;
  text-align: center;
  padding-block: 20px 20px 20px 20px;
  margin-block: 20px 20px 20px 20px;
}
.background-gradient {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  /* conic gradient cycles through your dark blues */
  background: conic-gradient(
    from 0deg at center,
    #0b0c17,
    #1f1f3f,
    #0b0c17,
    #1f1f3f
  );
  background-size: 200% 200%;
  /* soften and intensify colors */
  filter: blur(12px) saturate(180%);
  /* three simultaneous animations */
  animation:
    rotateBG 60s linear infinite,
    hueShift 36 ease-in-out infinite alternate,
    driftBG 40s ease-in-out infinite alternate;
  z-index: 1;
}

@keyframes rotateBG {
  0%   { transform: rotate(0deg)    scale(1);   }
  50%  { transform: rotate(180deg)  scale(1.05);}
  100% { transform: rotate(360deg)  scale(1);   }
}

@keyframes hueShift {
  0%   { filter: blur(12px) saturate(180%) hue-rotate(0deg);   }
  100% { filter: blur(12px) saturate(180%) hue-rotate(360deg); }
}

@keyframes driftBG {
  0%   { background-position: 0%   0%;   }
  50%  { background-position: 100% 100%; }
  100% { background-position: 0%   0%;   }
}

.pollstar-card { position:relative; z-index:2; max-width:1600px; margin:auto; padding:40px 40px 40px 40px; background:rgba(16,18,35,0.85); border-radius:16px; box-shadow:0 0 20px rgba(0,255,229,0.3); }
.header-bar { background:transparent!important; box-shadow:none!important; }
.title-glow { color:#00ffe5; text-shadow:0 0 4px #00ffe5,0 0 8px #1affff; }
.assistant-icon { font-size:28px; color:#54fff0; transition:transform .3s; }
.assistant-icon.listening { animation:pulse 1s infinite; }
@keyframes pulse { 0%,100%{transform:scale(1);}50%{transform:scale(1.2);} }
.stepper-glow .v-stepper__header { background:transparent!important; }
.search-row { margin-bottom:16px; }
.btn-glow { background:linear-gradient(45deg,#00ffe5,#1affff)!important; color:#0b0c17!important; font-weight:bold; text-transform:uppercase; box-shadow:0 4px 20px rgba(0,255,229,0.4); }
.btn-glow:hover { transform:translateY(-2px); }
.search-icon { color:#00ffe5; }
.neon-alert { background:rgba(0,255,229,0.1)!important; color:#00ffe5!important; box-shadow:0 0 10px rgba(0,255,229,0.3); }
.grid-title { font-family:'Share Tech Mono',monospace; color:#1affff; text-align:center; margin-bottom:12px; }
.artist-list-grid { margin-bottom:24px; }
.ai-artist-card {   background: rgba(10, 15, 30, 0.6);
  border-radius: 16px;
  padding: 20px;
  color: #ffffff;
  margin-top: 20px;
  transition: box-shadow 0.3s ease;
  font-size: 20px;
  line-height: 1.5; }
.ai-artist-card:hover { transform:scale(1.03); box-shadow:0 8px 20px rgba(0,255,229,0.3); }
.artist-name { font-size:18px; color:#fff; font-weight:700; }
.artist-genre { font-size:14px; color:#c2f7ff; }
.filter-row { margin-bottom:16px; }
.favorite-icon { position:absolute; top:8px; right:8px; color:rgba(255,255,255,0.5); transition:color .2s,transform .2s; }
.favorite-icon.saved { color:#ff5c5c; transform:scale(1.2); }
.artist-details-section .detail-label { font-size:14px; color:#b3f7ff; }
.artist-details-section .detail-value { font-size:16px; color:#fff; margin-bottom:8px; }
.link-glow { color:#00ffe5; text-decoration:none; transition:text-shadow .3s; }
.link-glow:hover { text-shadow:0 0 8px #00ffe5; }
.contact-carousel { padding:12px 0; }
.contact-panel { width:730px; padding:16px; background:rgba(5,3,12,0.7); border-radius:12px; text-align:center; transition:transform .2s,box-shadow .2s; }
.contact-panel:hover { transform:translateY(-4px); box-shadow:0 8px 20px rgba(0,255,229,0.3); }
.contact-name { color:#fff; font-weight:bold; margin-bottom:4px; }
.contact-territory { color:#b3f7ff; font-size:13px; }
.events-list li { color:#fff; margin-bottom:4px; }
.tour-map >>> .leaflet-container { border-radius:12px; box-shadow:0 0 16px rgba(0,255,229,0.3); }
</style>
